<template>
	<div class="image-editor">
		<div v-if="imgNode">

			<div class="img-wrapper">
				<div class="img-container"
				     v-bind:class='{
					     "-enable-hud": !drag.dragging && !misc.picking,
					     "-picking": misc.picking,
					     "-dragging": drag.dragging,
				     }'
				     style="text-align: center;">
					<canvas v-draw="drawObject()" class="canvas"
					        v-on:mousedown="startDrag"
					        v-on:touchstart="startDrag"
					        v-on:mousemove="onDrag"
					        v-on:touchmove.prevent="onDrag"
					        v-on:mouseup="stopDrag"
					        v-on:touchend="stopDrag"
					        v-on:mouseleave="stopDrag"
					></canvas>

					<div class="crosshair"></div>

					<div class="hud">

						<span class="group"
						      v-on:mousedown="startSlide"
						      v-on:touchstart="startSlide"
						      v-on:mousemove="onSlide"
						      v-on:touchmove="onSlide"
						      v-on:mouseup="stopSlide"
						      v-on:touchend="stopSlide"
						      v-on:mouseleave="stopSlide" >
							<input class="slider center-bottom"
							       v-bind:style='{ width: 2 / 3 * 100 + "%" }'
							       type="range"
							       min="-18000"
							       max="18000"
							       v-model="sliderrotation">
							<input class="slider center-right"
							       v-bind:style='{ width: 2 / 3 * 100 * this.ratio + "%" }'
							       type="range"
							       min="1"
							       max="100"
							       v-model="sliderScale">
							<button class="slider-button scale-grow"
							       @click="changeScaleLevel(1)">
							       +
							</button>
							<button class="slider-button scale-shrink"
							       @click="changeScaleLevel(-1)">
							       -
							</button>
						</span>
					</div>
				</div>
			</div>

			<div class="controls">
				<div class="row">
					<div class="col-lg-6 col-md-12 group sizes">
							<label class="label">Rahmen</label>
							<b-form-checkbox id="border-checkbox"
							                 v-model="border"
							                 value="j"
							                 unchecked-value="n">
								Rahmen hinzufügen
						    </b-form-checkbox>
					</div>
					<div class="col-lg-6 col-md-12 group sizes"
					     v-if="sizes.length > 1">
					    <label class="label">Seitenverhältnis</label>
						<b-button-group>
							<b-button v-for="size in sizes"
							          :key="size.name"
							          :variant="(size.name === selectedSize) ? 'primary': ''"
							          :data-name="size.name"
							          @click="handleSizeClick(size.name)">
								<div class="ratio-block" :style="getRatioBlockStyle(size)" :title="size.name"></div>
							</b-button>
						</b-button-group>
					</div>

					<div class="col-lg-6 col-md-12 group backgrounds"
					     v-if="backgrounds.length > 1">
					    <label class="label">Hintergrundfarbe</label>
						<b-button-group>
							<b-button v-if="background.value"
							          v-for="background in backgrounds"
							          :key="background.name"
							          :class="{ '-selected': background.name === selectedBackground}"
							          :style="`background-color: ${background.value};`"
							          :title="background.value"
							          :data-name="background.name"
							          @click="handleBackgroundColorClick(background.name)">
								<span>&nbsp;</span>
							</b-button>
							<div class="btn btn-secondary entered-color"
							     :class="{ '-selected': enteredBackground.name === selectedBackground}"
							     :style="`background-color: ${enteredBackground.value};`"
							     @click="handleBackgroundColorClick(enteredBackground.name)">
								<input type="text"
								       class="color-code"
								       v-model="enteredBackground.value"
								       @change="handleBackgroundColorClick(enteredBackground.name)" />
							</div>
							<b-button class="colorpicker"
							          v-on:click="colorPicker">
								<img class="image"
								     src="~@/assets/img/colorpicker.png" alt="Color" />
							</b-button>
						</b-button-group>
					</div>
				</div>
			</div>

		</div>
		<div v-else>
			<img class="placeholder"
			     src="~@/assets/img/placeholder-image.svg" />
		</div>
	</div>
</template>

<script>
	import * as TWEEN from 'es6-tween';
	import renderHelper from '@/helper/renderHelper';
	import convertHelper from '@/helper/convertHelper';

	// Map linear to log values
	const mappingHelper = (function mappingHelper() {
		// position will be between 1 and 100
		const minp = 1;
		const maxp = 100;

		const minv = Math.log(1);
		const maxv = Math.log(100);

		// calculate adjustment factor
		const scale = (maxv - minv) / (maxp - minp);

		function log(value) {
			return ((Math.log(value) - minv) / scale) + minp;
		}

		function exp(value) {
			return Math.exp(minv + (scale * (value - minp)));
		}

		return { log, exp };
	}());

	// Manage Animation
	const animateHelper = function animateHelper(from,
	                                             to,
	                                             callback,
	                                             namespace,
	                                             instantly,
	                                             completeCallback) {
		let animationFrame;

		if (namespace) {
			TWEEN.getAll()
				.filter(x => x.namespace === namespace)
				.forEach(x => TWEEN.remove(x));
		}

		if (instantly) {
			callback(to);
			if (completeCallback) {
				completeCallback();
			}
		} else {
			const animate = function animate(time) {
				TWEEN.update(time);
				animationFrame = requestAnimationFrame(animate);
			};

			new TWEEN.Tween(from).to(to, 300)
				.easing(TWEEN.Easing.Quadratic.Out)
				.on('update', callback)
				.on('complete', () => {
					if (completeCallback) {
						completeCallback();
					}
					cancelAnimationFrame(animationFrame);
				})
				.start()
				.namespace = namespace;

			animationFrame = requestAnimationFrame(animate);
		}
	};

	/* HTML-Color Helper */
	const HTMLColorOrElse = (colorValue, elseValue) => {
		if (colorValue.trim().match(/^#[0-9a-f]{6}$/i)) {
			return {
				r: parseInt(colorValue.substr(1, 2), 16),
				g: parseInt(colorValue.substr(3, 2), 16),
				b: parseInt(colorValue.substr(5, 2), 16),
				a: 1,
			};
		} else if (colorValue.trim().match(/^#[0-9-a-f]{3}/i)) {
			const redHex = `${colorValue.substr(1, 1)}${colorValue.substr(1, 1)}`;
			const greenHex = `${colorValue.substr(2, 1)}${colorValue.substr(2, 1)}`;
			const blueHex = `${colorValue.substr(3, 1)}${colorValue.substr(3, 1)}`;

			return {
				r: parseInt(redHex, 16),
				g: parseInt(greenHex, 16),
				b: parseInt(blueHex, 16),
				a: 1,
			};
		}

		return elseValue;
	};

	/* RGBA to HTML-Color Helper */
	const RGBAToHTMLColor = (color) => {
		if (typeof color !== 'string') {
			return false;
		}

		const parsedColor = {
			r: 0,
			g: 0,
			b: 0,
			a: 0,
		};

		const match = color.trim().replace(/\s/g, '').match(/^rgba\((\d+),(\d+),(\d+),(.+)\)$/);

		if (match) {
			parsedColor.r = parseInt(match[1], 10);
			parsedColor.g = parseInt(match[2], 10);
			parsedColor.b = parseInt(match[3], 10);
			parsedColor.a = parseInt(match[4], 10);
		}

		const rr = (`0${parseInt(parsedColor.r, 10).toString(16)}`).slice(-2);
		const gg = (`0${parseInt(parsedColor.g, 10).toString(16)}`).slice(-2);
		const bb = (`0${parseInt(parsedColor.b, 10).toString(16)}`).slice(-2);

		if (parsedColor.a === 0) {
			return 'transparent';
		}

		return `#${rr + gg + bb}`;
	};

	export default {
		name: 'ErcoImageEditorMolecule',
		props: {
			data: {
				type: Object,
			},
			backgrounds: {
				type: Array,
				default() {
					return [
						{
							name: 'Weiß',
							value: '#ffffff',
						},
						{
							name: 'Grau',
							value: '#aaaaaa',
						},
						{
							name: 'Schwarz',
							value: '#000000',
						},
						{
							name: 'Spezifisch',
							value: undefined,
						},
					];
				},
			},
		},
		computed: {

			// GUI components
			sliderScale: {
				get() {
					return mappingHelper.log(this.img.scale * 10);
				},
				set(value) {
					this.setScale(mappingHelper.exp(value) / 10);
					this.handleChange();
				},
			},
			sliderrotation: {
				get() {
					return this.img.rotation * 100;
				},
				set(value) {
					this.setRotation(value / 100);
					this.handleChange();
				},
			},
			selectedSize: {
				get() {
					const size = this.sizes.find(x => x.width === this.img.width
						&& x.height === this.img.height);
					return size && size.name;
				},
				set(value) {
					const size = this.sizes.find(x => x.name === value);
					this.setSize(size.width, size.height);
					this.handleChange();
				},
			},
			selectedBackground: {
				get() {
					const background = this.backgrounds.find(x => x.value === this.img.background);
					return background && background.name;
				},
				set(value) {
					const background =
						(value === this.enteredBackground.name) ?
							this.enteredBackground :
							this.backgrounds.find(x => x.name === value);
					this.setBackground(background.value);
					this.handleChange();
				},
			},

			// additional img props
			ratio() {
				return this.img.height / (this.img.width || 1);
			},
			initialScale() {
				return (this.ratio < this.imgRatio) ? 1 : this.ratio / (this.imgRatio || 1);
			},
			imgRatio() {
				return this.imgNode.height / (this.imgNode.width || 1);
			},
		},
		data() {
			return {
				border: 'n',
				// coord
				img: {
					left: undefined,
					top: undefined,
					scale: undefined,
					rotation: undefined,
					width: undefined,
					height: undefined,
					background: undefined,
				},

				// image loaded
				imgNode: undefined,

				// dragging
				drag: {
					prev: { x: 0, y: 0 },
					dragging: false,
				},

				// misc
				misc: {
					background: undefined,
					slideDown: false,
					sliding: false,
					color: undefined,
					picking: false,
				},

				// sizes
				sizes: [
					{
						name: '1:1',
						width: 550,
						height: 550,
					},
					{
						name: '2:1',
						width: 1100,
						height: 550,
					},
					{
						name: '4:3',
						width: 1100,
						height: 825,
					},
				],

				enteredBackground: {
					name: 'entered',
					value: '#FF00FF',
				},
			};
		},
		methods: {

			// draw object
			drawObject() {
				this.img.border = this.border === 'j';
				return renderHelper.getCanvas(this.imgNode, this.img, this.misc.background);
			},

			// update data
			setId(value) {
				if (!value || (this.imgNode && this.imgNode.src === value)) {
					if (!value) {
						this.imgNode = undefined;
					}
					return;
				}

				// load image node
				const img = new Image();
				img.crossOrigin = 'Anonymous';
				img.src = value;
				img.onerror = () => {
					this.imgNode = undefined;
				};
				img.onload = () => {
					this.imgNode = img;

					const converted = convertHelper.importData(this.data, this.imgNode);

					this.setBackground(converted.background);
					this.setRotation(converted.rotation);
					this.setSize(converted.width, converted.height);
					this.setScale(converted.scale);
					this.setPosition(converted.left, converted.top);
					this.setBorder(this.data.B_Rahmen);
				};
			},

			setBorder(border) {
				this.border = border;
			},

			setPosition(left = 0, top = 0) {
				const instantly = typeof this.img.left === 'undefined' || this.drag.dragging || (this.img.left === left && this.img.top === top);
				const maxLeft = 0.5 + ((this.img.scale) / 2);
				const maxTop  = 0.5 + ((this.img.scale * (this.imgRatio / this.ratio)) / 2);

				animateHelper({
					l: this.img.left,
					t: this.img.top,
				}, {
					l: Math.min(Math.abs(left), maxLeft) * Math.sign(left),
					t: Math.min(Math.abs(top), maxTop) * Math.sign(top),
				}, ({ l, t }) => {
					this.img.left = l;
					this.img.top = t;
				},
				'position', instantly);
			},


			setScale(scale = this.initialScale) {
				const instantly = (typeof this.img.scale === 'undefined' || this.misc.sliding || this.img.scale === scale);

				animateHelper({
					s: this.img.scale,
				}, {
					s: scale,
				}, ({ s }) => {
					this.img.scale = s;
				},
				'scale', instantly);
			},

			setSize(width, height) {
				const instantly = (!this.img.width && !this.img.height) ||
					(width === this.img.width && height === this.img.height);
				const size = this.sizes.find(x => x.width === width && x.height === height) || this.sizes[0];

				// Adjust scale differences
				if (this.img.scale) {
					const diff = (!width && !height) ? 1 : this.img.scale / this.initialScale;
					this.setScale(this.getInitialScaleFor(size.width, size.height) * diff);
				}

				animateHelper({
					w: this.img.width,
					h: this.img.height,
				}, {
					w: size.width,
					h: size.height,
				}, ({ w, h }) => {
					this.img.width = w;
					this.img.height = h;
				},
				'size', instantly);
			},
			setBackground(value) {
				const val = value || this.backgrounds[0].value;
				const instantly = !this.img.background || this.img.background === val;

				if (this.enteredBackground.value !== val &&
					!this.backgrounds.find(x => x.value === val)) {
					if (!this.backgrounds.find(x => x.name === 'custom')) {
						this.backgrounds.push({ name: 'custom' });
					}

					this.backgrounds.find(x => x.name === 'custom').value = val;
				}

				const initBackground = this.img.background || val;
				const background =
					this.enteredBackground.value === val ?
						this.enteredBackground :
						this.backgrounds.find(x => x.value === val);

				const from = HTMLColorOrElse(initBackground, { r: 0, g: 0, b: 0, a: 0 });
				const to = HTMLColorOrElse(background.value, { r: 0, g: 0, b: 0, a: 0 });

				animateHelper(from, to, ({ r, g, b, a }) => {
					this.img.background = `rgba(${r.toFixed()}, ${g.toFixed()}, ${b.toFixed()}, ${a.toFixed(2)})`;
				},
				'background', instantly, () => {
					this.img.background = RGBAToHTMLColor(this.img.background);
				});
			},

			setSpecialBackground(value = this.img.background) {
				const start = this.misc.background || this.img.background;
				const stop = value;

				const from = HTMLColorOrElse(start, { r: 0, g: 0, b: 0, a: 0 });
				const to = HTMLColorOrElse(stop, { r: 0, g: 0, b: 0, a: 0 });

				animateHelper(from, to, ({ r, g, b, a }) => {
					const color = `rgba(${r.toFixed()}, ${g.toFixed()}, ${b.toFixed()}, ${a.toFixed(2)})`;
					this.misc.background = (color !== this.img.background) && color;
				},
				'specialbackground', false, () => {
					if (value === this.img.background) {
						this.misc.background = false;
					} else {
						this.misc.background = RGBAToHTMLColor(this.misc.background);
					}
				});
			},

			setEnteredBackground(value = this.misc.color) {
				this.enteredBackground.value = value;
				this.handleBackgroundColorClick(this.enteredBackground.name);
				this.selectedBackground = 'entered';
			},

			setRotation(value = 0) {
				const instantly = this.misc.sliding || typeof this.img.rotation === 'undefined' || this.img.rotation === value;

				animateHelper({
					rotation: this.img.rotation || 0,
				}, {
					rotation: ((value + 180) % 360) - 180,
				}, ({ rotation }) => {
					this.img.rotation = rotation;
				},
				'rotation', instantly);
			},

			setSizes() {
				const w = parseInt(this.data.base_width, 10);
				const h = parseInt(this.data.base_height, 10);
				const ratios = this.data.ratios && JSON.parse(this.data.ratios);
				const dimension = this.data.dimension;

				if (!w && !h) return;

				const newSizes = [
					{
						name: 'Original',
						width: w,
						height: h,
					},
				];

				if (ratios) {
					ratios.forEach((x) => {
						if (dimension === 'height') {
							newSizes.push({
								name: `${x}`,
								width: h * x,
								height: h,
								ratio: x,
							});
						} else {
							newSizes.push({
								name: `${x}`,
								width: w,
								height: w / x,
								ratio: x,
							});
						}
					});
				}
				this.sizes = newSizes;
			},

			getInitialScaleFor(width, height) {
				const ratio = height / width;

				return (ratio < this.imgRatio) ? 1 : ratio / this.imgRatio;
			},

			resetData() {
				this.setBackground();
				this.setRotation();
				this.setSize();
				this.setPosition();

				this.handleChange();
			},

			colorPicker() {
				this.misc.picking = true;
			},


			// dragging
			startDrag(e) {
				const ev = e.changedTouches ? e.changedTouches[0] : e;

				if (this.misc.picking && this.misc.color) {
					this.setBackground(this.misc.color);
					this.setEnteredBackground(this.misc.color);
					this.misc.color = undefined;
					this.misc.picking = false;
				} else {
					this.drag.dragging = true;
					this.setSpecialBackground('#ff00ff');

					this.drag.prev.x = ev.pageX;
					this.drag.prev.y = ev.pageY;
				}
			},
			onDrag(e) {
				const ev = e.changedTouches ? e.changedTouches[0] : e;


				if (this.misc.picking) {
					// color under cursor
					const el = ev.target;
					const b  = el.getBoundingClientRect();
					const x  = ev.pageX - (b.left + window.scrollX);
					const y  = ev.pageY - (b.top + window.scrollY);

					const dx = parseInt((x / b.width) * el.width, 10);
					const dy = parseInt((y / b.height) * el.height, 10);

					const p = el.getContext('2d').getImageData(dx, dy, 1, 1).data;

					const rr = (`0${parseInt(p[0], 10).toString(16)}`).slice(-2);
					const gg = (`0${parseInt(p[1], 10).toString(16)}`).slice(-2);
					const bb = (`0${parseInt(p[2], 10).toString(16)}`).slice(-2);

					this.misc.color = `#${rr + gg + bb}`;
					this.setEnteredBackground(this.misc.color);
				}

				if (this.drag.dragging) {
					const width  = ev.target.offsetWidth;
					const height = ev.target.offsetHeight;
					const left = (ev.pageX - this.drag.prev.x) / width;
					const top  = (ev.pageY - this.drag.prev.y) / height;

					this.setPosition(this.img.left + left, this.img.top + top);

					this.drag.prev.x = ev.pageX;
					this.drag.prev.y = ev.pageY;
				}
			},
			stopDrag() {
				if (this.drag.dragging) {
					this.drag.dragging = false;
					this.setSpecialBackground();
					this.drag.prev = { x: 0, y: 0 };

					this.handleChange();
				}
			},

			// sliding
			startSlide() {
				this.misc.slideDown = true;
				setTimeout(() => {
					if (this.misc.slideDown) {
						this.setSpecialBackground('#ff00ff');
					}
				}, 100);
			},
			onSlide() {
				if (this.misc.slideDown) {
					this.misc.sliding = true;
				}
			},
			stopSlide() {
				if (this.misc.slideDown) {
					this.misc.sliding = false;
					this.misc.slideDown = false;
					this.setSpecialBackground();
				}
			},

			handleSizeClick(sizeName) {
				this.selectedSize = sizeName;
			},

			handleBackgroundColorClick(bgColorName) {
				this.selectedBackground = bgColorName;
			},

			getRatioBlockStyle(size) {
				const baseRem = 1.2;
				const height = size.height;
				const width = size.width;
				const blockHeight = baseRem;
				const blockWidth = parseFloat((baseRem * (width / height)).toFixed(3));
				return `height: ${blockHeight}rem; width: ${blockWidth}rem;`;
			},

			handleSave() {
				if (!this.imgNode) {
					return;
				}
				const newSidebarValue = Object.assign({}, this.$store.state.sidebar.context);

				if (newSidebarValue.value && newSidebarValue.value[this.data.key]) {
					delete newSidebarValue.value[this.data.key].value;
				}

				newSidebarValue.value[this.data.key] = convertHelper.exportData(this.img, this.imgNode);
				newSidebarValue.value[this.data.key].U_Position = this.data.U_Position;
				newSidebarValue.value[this.data.key].sprache = this.data.S_Sprachkuerzel;
				newSidebarValue.value[this.data.key].B_Rahmen = this.border;

				const newSidebarOutputData =
					Object.assign({},
				                  this.data,
				                  newSidebarValue.value[this.data.key]);
				this.$store.dispatch('updateSidebar', {
					context: newSidebarValue,
					outputData: newSidebarOutputData,
				});
			},

			handleChange() {
				this.$store.dispatch('updateSidebar', {
					applied: false,
				});
			},

			/* Sidebar - Actions */
			handleApply() {
				this.handleSave();
			},

			triggerReset() {
				this.resetData();
			},

			changeScaleLevel(val) {
				this.sliderScale = Math.min(100, Math.max(1, this.sliderScale + val));
			},
		},
		mounted() {
			// set available sizes
			this.setSizes();

			// set image id, other data will follow
			this.setId(this.data.value);
		},
		watch: {
			data() {
				// set available sizes
				this.setSizes();

				// set image id, other data will follow
				this.setId(this.data.value);
			},
		},
		directives: {
			draw(el, binding) {
				const getDrawing = binding.value;
				if (!getDrawing.width) return;

				window.requestAnimationFrame(() => {
					el.width = getDrawing.width;
					el.height = getDrawing.height;
					el.getContext('2d').drawImage(getDrawing, 0, 0);
				});
			},
		},
	};

</script>

<style lang="scss">

@mixin transparent-bg-image {
	background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABIAAAASCAIAAADZrBkAAAAAHUlEQVR4AWM4gxv8xw2GqLZRbXjk8Jg4NLWNagMATRdnkPAduCkAAAAASUVORK5CYII=);
}

.image-editor {
	& > .image {
		width: 100%;
	}
}


.placeholder {
	width: 100%;
}

.canvas {
	display: block;
	max-width: 100%;
	min-width: 250px;
}

.img-wrapper {
	text-align: center;

	.img-container {
		@include transparent-bg-image;

		border: 1px solid rgba(220, 220, 220, 1);
		display: inline-block;
		padding-right: 0.1px;
		position: relative;
		overflow: hidden;
		background-repeat: repeat;

		& .group {
			display: block;
		}

		& > .canvas:hover {
			cursor: move;
		}
		&.-picking > .canvas:hover {
			cursor: crosshair;
		}


		&.-dragging > .crosshair {
			opacity: 1;
		}
		.crosshair {
			transition: all .25s;
			opacity: 0;

			&:before {
				width: 100%;
				top: 50%;
			}

			&:after {
				height: 100%;
				left: 50%;
			}

			&:before,
			&:after {
				content: '';
				display: block;
				position: absolute;
				border: 1px solid lightblue;
				border-width: 1px 1px 0px 0px;
				margin: -0.5px;
			}
		}


		& > .hud > .group > * {
			opacity: 0;
			pointer-events: none;
			transition: all .25s;
		}

		&.-enable-hud:hover > .hud > .group > * {
			opacity: 1;
			pointer-events: all;
		}

		& > .crosshair,
		& > .hud {
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			pointer-events: none;
		}
	}
}


/* slider vertical */

.slider {
	background-color: transparent;
}

.slider-button {
	background-color: transparent;
	position: absolute;
	border: 0;
	right: 0.4rem;
	margin: 0 !important;
	width: 1.25rem;
	height: 1.25rem;
	padding: 0rem;
	cursor: pointer;
	color: rgb(50, 50, 50);

	&.scale-grow {
		top: 8%;
	}

	&.scale-shrink {
		bottom: 9%;
	}
}

.center-right {
	position: absolute;
	margin: 0 !important;
	right: 1rem;
	top: 50%;
	transform: translate(50%, -50%) rotate(-90deg);
}

.center-bottom {
	position: absolute;
	margin: 0 !important;
	margin-bottom:  3px !important;
	bottom: 1rem;
	left: 50%;
	transform: translate(-50%, 50%);
}

.controls {
	& > .row > .group {
		margin-top: 1rem;

		&.backgrounds {
			& .btn-group {
				& > .btn {
					&.-selected {
						/* border: 2px solid rgba(1, 84, 155, 2); */
					}

					&.colorpicker {
						& > .image {
							height: 1.2rem;
						}
					}

					&[data-name="custom"][title="transparent"] {
						@include transparent-bg-image;
					}
				}

				.entered-color {
					cursor: pointer;
					padding: 0.5rem;
					padding-left: 2rem;

					.color-code {
						border-radius: 2px;
						border: 1px solid rgba(200, 200, 200, 0.3);
						width: 5.5rem;
						padding: 0 0.5rem;
						background-color: rgba(255, 255, 255, 0.9);
					}
				}
			}
		}

		&.reset {
			margin-top: 2rem;
		}

		& > .label {
			display: block;
			font-weight: bold;
		}

		& .ratio-block {
			background-color: rgba(41, 43, 44, 0.3);
			border: 1px solid rgba(41, 43, 44, 0.5);
		}
	}
}




// Styling Cross-Browser Compatible Range Inputs with Sass
// Github: https://github.com/darlanrod/input-range-sass
// Author: Darlan Rod https://github.com/darlanrod
// Version 1.4.1
// MIT License

$track-color: rgba(128, 128, 128, .5) !default;
$thumb-color: rgba(255, 255, 255, .5) !default;

$thumb-radius: 2rem !default;
$thumb-height: 1rem !default;
$thumb-width: 1rem !default;
$thumb-shadow-size: 0 !default;
$thumb-shadow-blur: 0 !default;
$thumb-shadow-color: transparent !default;
$thumb-border-width: 0 !default;
$thumb-border-color: transparent !default;

$track-width: 50% !default;
$track-height: 1rem !default;
$track-shadow-size: 0 !default;
$track-shadow-blur: 0 !default;
$track-shadow-color: transparent !default;
$track-border-width: 0 !default;
$track-border-color: transparent !default;

$track-radius: 1rem !default;
$contrast: 5% !default;

@mixin shadow($shadow-size, $shadow-blur, $shadow-color) {
  box-shadow: $shadow-size $shadow-size $shadow-blur $shadow-color, 0 0 $shadow-size lighten($shadow-color, 5%);
}

@mixin track {
  cursor: pointer;
  height: $track-height;
  transition: all .25s ease;
  width: inherit;
}

@mixin thumb {
  @include shadow($thumb-shadow-size, $thumb-shadow-blur, $thumb-shadow-color);
  background: $thumb-color;
  border: $thumb-border-width solid $thumb-border-color;
  border-radius: $thumb-radius;
  cursor: pointer;
  height: $thumb-height;
  width: $thumb-width;
}

[type='range'] {
  -webkit-appearance: none;
  margin: $thumb-height / 2 0;
  width: $track-width;

  &:focus {
    outline: 0;

    &::-webkit-slider-runnable-track {
      background: lighten($track-color, $contrast);
    }

    &::-ms-fill-lower {
      background: $track-color;
    }

    &::-ms-fill-upper {
      background: lighten($track-color, $contrast);
    }
  }

  &::-webkit-slider-runnable-track {
    @include track;
    @include shadow($track-shadow-size, $track-shadow-blur, $track-shadow-color);
    background: $track-color;
    border: $track-border-width solid $track-border-color;
    border-radius: $track-radius;
  }

  &::-webkit-slider-thumb {
    @include thumb;
    -webkit-appearance: none;
    margin-top: ((-$track-border-width * 2 + $track-height) / 2) - ($thumb-height / 2);
  }

  &::-moz-range-track {
    @include track;
    width: 100%;
    @include shadow($track-shadow-size, $track-shadow-blur, $track-shadow-color);
    background: $track-color;
    border: $track-border-width solid $track-border-color;
    border-radius: $track-radius;
  }

  &::-moz-range-thumb {
    @include thumb;
  }

  &::-ms-track {
    @include track;
    background: transparent;
    border-color: transparent;
    border-width: ($thumb-height / 2) 0;
    color: transparent;
  }

  &::-ms-fill-lower {
    @include shadow($track-shadow-size, $track-shadow-blur, $track-shadow-color);
    background: darken($track-color, $contrast);
    border: $track-border-width solid $track-border-color;
    border-radius: $track-radius * 2;
  }

  &::-ms-fill-upper {
    @include shadow($track-shadow-size, $track-shadow-blur, $track-shadow-color);
    background: $track-color;
    border: $track-border-width solid $track-border-color;
    border-radius: $track-radius * 2;
  }

  &::-ms-thumb {
    @include thumb;
    margin-top: 0;
  }
}

</style>



// WEBPACK FOOTER //
// ErcoImageEditor.vue?40c42db8